import { useMemo } from "react"
import styled, { css } from "styled-components"

import { ApiTypes } from "types"

import { formatRuntimeDuration } from "utils/formatRuntimeDuration"

import { BarChartSettings } from "./getBarChartsSettings"

const RuntimeBoxesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 30px 0 0 25px;

  @media (max-width: 768px) {
    margin: 0;
  }
`

const RuntimeBox = styled.div<{ isEmpty?: boolean }>`
  padding: 16px 10px;
  background-color: #f8fafc;
  min-width: 200px;
  flex: 1;
  box-sizing: border-box;

  ${({ isEmpty }) =>
    isEmpty &&
    css`
      visibility: hidden;
      height: 1px;
      padding: 0 10px;
    `}
`

const RuntimeBoxTitle = styled.div`
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 8px;
  color: #64748b;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  gap: 6px;
`

const ColorBox = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  background-color: ${({ $color }) => $color};
`

const RuntimeBoxDuration = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
`

interface RuntimeBoxesProps {
  barChartSettings: BarChartSettings
  historicalData: ApiTypes.Res.MonitoringHistoricalDataBucket[]
}

interface StageConfig {
  key: keyof ReturnType<typeof calculateRuntimeData>
  title: string
  color: string
  requiresCoolingStage1?: boolean
}

const STAGE_CONFIG: StageConfig[] = [
  { key: "coolingStage1", title: "Cooling - Stage 1", color: "#92c5fd" },
  { key: "coolingStage2", title: "Cooling - Stage 2", color: "#2463eb", requiresCoolingStage1: true },
  { key: "heatingStage1", title: "Heating - Stage 1", color: "#fca5a5" },
  { key: "heatingStage2", title: "Heating - Stage 2", color: "#ef4444" },
  { key: "heatingStage3", title: "Heating - Stage 3", color: "#b91c1c" },
]

const calculateRuntimeData = (
  historicalData: ApiTypes.Res.MonitoringHistoricalDataBucket[],
  barChartSettings: BarChartSettings,
) => {
  const totals = historicalData.reduce(
    (acc, item) => {
      acc.coolingStage1 += item["filters#current_cooling_stage_count"].buckets.count1.doc_count
      acc.coolingStage2 += item["filters#current_cooling_stage_count"].buckets.count2.doc_count
      acc.heatingStage1 += item["filters#current_heating_stage_count"].buckets.count1.doc_count
      acc.heatingStage2 += item["filters#current_heating_stage_count"].buckets.count2.doc_count
      acc.heatingStage3 += item["filters#current_heating_stage_count"].buckets.count3.doc_count
      return acc
    },
    {
      coolingStage1: 0,
      coolingStage2: 0,
      heatingStage1: 0,
      heatingStage2: 0,
      heatingStage3: 0,
    },
  )

  const convertToMinutes = (value: number) => {
    if (barChartSettings.dataType === "minutes") {
      return value
    }

    return value * 60
  }

  const coolingStage1Minutes = convertToMinutes(totals.coolingStage1 / barChartSettings.hourlyDivider)
  const coolingStage2Minutes = convertToMinutes(totals.coolingStage2 / barChartSettings.hourlyDivider)
  const heatingStage1Minutes = convertToMinutes(totals.heatingStage1 / barChartSettings.hourlyDivider)
  const heatingStage2Minutes = convertToMinutes(totals.heatingStage2 / barChartSettings.hourlyDivider)
  const heatingStage3Minutes = convertToMinutes(totals.heatingStage3 / barChartSettings.hourlyDivider)

  const totalRuntimeMinutes =
    coolingStage1Minutes + coolingStage2Minutes + heatingStage1Minutes + heatingStage2Minutes + heatingStage3Minutes

  return {
    totalRuntime: totalRuntimeMinutes,
    coolingStage1: coolingStage1Minutes,
    coolingStage2: coolingStage2Minutes,
    heatingStage1: heatingStage1Minutes,
    heatingStage2: heatingStage2Minutes,
    heatingStage3: heatingStage3Minutes,
  }
}

export const RuntimeBoxes = ({ barChartSettings, historicalData }: RuntimeBoxesProps) => {
  const runtimeData = useMemo(
    () => calculateRuntimeData(historicalData, barChartSettings),
    [historicalData, barChartSettings],
  )

  if (runtimeData.totalRuntime === 0) {
    return null
  }

  const boxes: Array<{ title: string; duration: string; color?: string }> = [
    {
      title: "Total Runtime",
      duration: formatRuntimeDuration(runtimeData.totalRuntime),
    },
  ]

  const hasCoolingStage1 = runtimeData.coolingStage1 > 0
  for (const stage of STAGE_CONFIG) {
    const runtime = runtimeData[stage.key]
    if (runtime > 0) {
      if (stage.requiresCoolingStage1 && !hasCoolingStage1) {
        continue
      }
      boxes.push({
        title: stage.title,
        duration: formatRuntimeDuration(runtime),
        color: stage.color,
      })
    }
  }

  if (boxes.length === 0) {
    return null
  }

  return (
    <RuntimeBoxesContainer>
      {boxes.map(box => (
        <RuntimeBox key={box.title}>
          <RuntimeBoxTitle>
            {box.color && <ColorBox $color={box.color} />}
            {box.title}
          </RuntimeBoxTitle>
          <RuntimeBoxDuration>{box.duration}</RuntimeBoxDuration>
        </RuntimeBox>
      ))}
      <RuntimeBox isEmpty />
      <RuntimeBox isEmpty />
      <RuntimeBox isEmpty />
    </RuntimeBoxesContainer>
  )
}
